import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { useAPIGetStockIn } from "../api/api-get-stock-in";
import { Column } from "primereact/column";
import StockInFilter from "./stock-in-filter";
import {
  genTemplateBodyNumber,
  genTemplateBodyText,
} from "../../../components/utils/TableTemplateBodyGenerator";
import { Paginator } from "primereact/paginator";
import { Button } from "primereact/button";
import { format } from "date-fns";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { useSelector } from "react-redux";
import useIsMobile from "../../../hooks/useIsMobile";

const StockInList = forwardRef((props, ref) => {
  const { onClickRowDelete, onClickRowEdit, onClickAddNew } = props;
  const isMobile = useIsMobile();
  const employee = useSelector((state) => state.auth.employee);

  const {
    loading,
    loadingPending,
    listData,
    listDataPending,
    fetchListData,
    filterValue,
    setFilterValue,
    filterPendingValue,
    setFilterPendingValue,
    handleOnClickRefresh,
    handleOnPage,
    paging,
    handleOnUpdateViewRow,
    handleOnDeleteViewRow,
    handleVerifOwner,
    handleVerifPayment,
    handleVerifShipment,
    handleVerifDone,
  } = useAPIGetStockIn();

  useImperativeHandle(ref, () => ({
    handleOnUpdateViewRow: (row) => {
      handleOnUpdateViewRow(row);
    },
    handleOnDeleteViewRow: (row) => {
      handleOnDeleteViewRow(row);
    },
  }));

  // use effect section

  useEffect(() => {
    fetchListData({ page: paging.page, take: paging.take });
  }, []);

  // Verification functions
  const confirmVerifOwner = (row) => {
    confirmDialog({
      message: `Are you sure you want to verify this stock (${row.number})?`,
      header: 'Owner Verification Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-success',
      accept: () => {
        handleVerifOwner(row.id, () => {
          fetchListData({ page: paging.page, take: paging.take });
        });
      },
    });
  };

  const confirmVerifPayment = (row) => {
    confirmDialog({
      message: `Are you sure you want to verify payment for this stock (${row.number})?`,
      header: 'Payment Verification Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-success',
      accept: () => {
        handleVerifPayment(row.id, () => {
          fetchListData({ page: paging.page, take: paging.take });
        });
      },
    });
  };

  const confirmVerifShipment = (row) => {
    confirmDialog({
      message: `Are you sure you want to verify shipment for this stock (${row.number})?`,
      header: 'Shipment Verification Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-success',
      accept: () => {
        handleVerifShipment(row.id, () => {
          fetchListData({ page: paging.page, take: paging.take });
        });
      },
    });
  };

  const confirmVerifDone = (row) => {
    confirmDialog({
      message: `Are you sure you want to mark this stock (${row.number}) as done?`,
      header: 'Done Verification Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-success',
      accept: () => {
        handleVerifDone(row.id, () => {
          fetchListData({ page: paging.page, take: paging.take });
        });
      },
    });
  };

  //  custom ui section

  // PART_TEMPLATE_BODY_GENERATOR
  const WarehouseBodyTemplate = (row) => {
    const { data_warehouse, data_branch } = row;

    return <div className="col-start-start gap-1">
      <span>{data_warehouse?.name}</span>
      <span className="text-sm font-semibold">Branch: {data_branch?.name}</span>
    </div>
  };
  const NumberBodyTemplate = (row) => {
    const { number, transaction_date } = row;

    return <div className="col-start-start gap-1">
      <span>{number}</span>
      <span className="text-sm font-semibold">Date: {format(new Date(transaction_date), "dd MMMM yyyy")}</span>
    </div>
  };

  const TimelineBodyTemplate = (row) => {
    const {
      data_created_by,
      created_by_datetime,

      data_verif_owner,
      verif_owner_datetime,

      data_verif_payment_by,
      verif_payment_datetime,

      data_verif_shipment_by,
      verif_shipment_datetime,

      data_verif_done_by,
      verif_done_datetime,
    } = row;

    const isOwner = employee?.user_type === 2;
    const isFinance = employee?.user_type === 11;
    const isAdmin = employee?.user_type === 4;

    const ownerVerified = !!verif_owner_datetime;
    const paymentVerified = !!verif_payment_datetime;
    const shipmentVerified = !!verif_shipment_datetime;
    const doneVerified = !!verif_done_datetime;

    // // --- Unlock rules (sequential + owner only) ---
    // const canVerifyOwner = isOwner && !ownerVerified;
    // const canVerifyPayment = isFinance && ownerVerified && !paymentVerified;
    // const canVerifyShipment = isAdmin && paymentVerified && !shipmentVerified;
    // const canVerifyDone = isAdmin && shipmentVerified && !doneVerified;

    // --- Unlock rules (sequential + owner only) ---
    const canVerifyOwner = isAdmin && !ownerVerified;
    const canVerifyPayment = isAdmin && ownerVerified && !paymentVerified;
    const canVerifyShipment = isAdmin && paymentVerified && !shipmentVerified;
    const canVerifyDone = isAdmin && shipmentVerified && !doneVerified;

    // --- New universal status generator ---
    const getStepStatusInfo = (isVerified, canVerify, step) => {
      const map = {
        owner: {
          icon: "pi-user",
          verifiedText: "Owner Verified",
          verifyText: "Verify Owner",
          pendingText: "Pending",
          bubbleVerified: "bg-green-600",
          bubbleVerify: "bg-green-600 shadow-lg",
          bubblePending: "bg-gray-300",
          label: "text-green-700",
          connector: "bg-green-600",
          bubbleHover: "cursor-pointer hover:bg-green-700 hover:shadow-xl transition-all",
        },
        payment: {
          icon: "pi-wallet",
          verifiedText: "Payment Verified",
          verifyText: "Verify Payment",
          pendingText: "Pending",
          bubbleVerified: "bg-blue-600",
          bubbleVerify: "bg-blue-600 shadow-lg",
          bubblePending: "bg-gray-300",
          label: "text-blue-700",
          connector: "bg-blue-600",
          bubbleHover: "cursor-pointer hover:bg-blue-700 hover:shadow-xl transition-all",
        },
        shipment: {
          icon: "pi-truck",
          verifiedText: "Shipment Verified",
          verifyText: "Verify Shipment",
          pendingText: "Pending",
          bubbleVerified: "bg-yellow-600",
          bubbleVerify: "bg-yellow-600 shadow-lg",
          bubblePending: "bg-gray-300",
          label: "text-yellow-700",
          connector: "bg-yellow-600",
          bubbleHover: "cursor-pointer hover:bg-yellow-700 hover:shadow-xl transition-all",
        },
        done: {
          icon: "pi-check-circle",
          verifiedText: "Completed",
          verifyText: "Finish Task",
          pendingText: "Pending",
          bubbleVerified: "bg-purple-600",
          bubbleVerify: "bg-purple-600 shadow-lg",
          bubblePending: "bg-gray-300",
          label: "text-purple-700",
          connector: "bg-purple-600",
          bubbleHover: "cursor-pointer hover:bg-purple-700 hover:shadow-xl transition-all",
        },
      };

      const cfg = map[step] || map.owner;

      if (isVerified) {
        return {
          icon: cfg.icon,
          status: cfg.verifiedText,
          bubble: cfg.bubbleVerified,
          label: cfg.label,
          connector: cfg.connector,
          bubbleHover: "",
        };
      }

      if (canVerify) {
        return {
          icon: cfg.icon,
          status: cfg.verifyText,
          bubble: cfg.bubbleVerify,
          label: cfg.label,
          connector: cfg.connector,
          bubbleHover: cfg.bubbleHover,
        };
      }

      return {
        icon: cfg.icon,
        status: cfg.pendingText,
        bubble: cfg.bubblePending,
        label: "text-gray-400",
        connector: "bg-gray-300",
        bubbleHover: "",
      };
    };

    // Assign status to each stage
    const ownerInfo = getStepStatusInfo(ownerVerified, canVerifyOwner, "owner");
    const paymentInfo = getStepStatusInfo(paymentVerified, canVerifyPayment, "payment");
    const shipmentInfo = getStepStatusInfo(shipmentVerified, canVerifyShipment, "shipment");
    const doneInfo = getStepStatusInfo(doneVerified, canVerifyDone, "done");

    return (
      <div className="col-start-start gap-2 py-2 w-full p-2">
        <div className="flex items-start justify-between w-full relative">

          {/* CREATED */}
          <div className="flex flex-col items-center gap-1 flex-1 min-w-0">
            <div className="w-10 h-10 rounded-full bg-gray-500 flex items-center justify-center">
              <i className="pi pi-user text-white text-sm"></i>
            </div>

            <span className="text-xs font-semibold text-gray-700 text-center">
              Created
            </span>

            <span className="text-sm font-semibold text-center break-words px-1">
              {data_created_by?.name}
            </span>

            <span className="text-xs font-semibold text-gray-600">
              {created_by_datetime
                ? format(new Date(created_by_datetime), "dd/MM/yy HH:mm")
                : "-"}
            </span>
          </div>

          {/* CONNECT → OWNER */}
          <div className={`flex-1 h-1 ${ownerInfo.connector} mt-5 mx-2`} />

          {/* OWNER */}
          <div className="flex flex-col items-center gap-1 flex-1 min-w-0">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center ${ownerInfo.bubble} ${ownerInfo.bubbleHover}`}
              onClick={() => canVerifyOwner && confirmVerifOwner(row)}
            >
              <i className={`pi ${ownerInfo.icon} text-white text-sm`}></i>
            </div>

            <span className={`text-xs font-semibold text-center ${ownerInfo.label}`}>
              {ownerInfo.status}
            </span>

            {ownerVerified && (
              <>
                <span className="text-sm font-semibold text-center break-words px-1">
                  {data_verif_owner?.name}
                </span>
                <span className="text-xs font-semibold text-gray-600">
                  {verif_owner_datetime
                    ? format(new Date(verif_owner_datetime), "dd/MM/yy HH:mm")
                    : "-"}
                </span>
              </>
            )}
          </div>

          {/* CONNECT → PAYMENT */}
          <div className={`flex-1 h-1 ${paymentInfo.connector} mt-5 mx-2`} />

          {/* PAYMENT */}
          <div className="flex flex-col items-center gap-1 flex-1 min-w-0">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center ${paymentInfo.bubble} ${paymentInfo.bubbleHover}`}
              onClick={() => canVerifyPayment && confirmVerifPayment(row)}
            >
              <i className={`pi ${paymentInfo.icon} text-white text-sm`}></i>
            </div>

            <span className={`text-xs font-semibold text-center ${paymentInfo.label}`}>
              {paymentInfo.status}
            </span>

            {paymentVerified && (
              <>
                <span className="text-sm font-semibold text-center break-words px-1">
                  {data_verif_payment_by?.name}
                </span>
                <span className="text-xs font-semibold text-gray-600">
                  {verif_payment_datetime
                    ? format(new Date(verif_payment_datetime), "dd/MM/yy HH:mm")
                    : "-"}
                </span>
              </>
            )}
          </div>

          {/* CONNECT → SHIPMENT */}
          <div className={`flex-1 h-1 ${shipmentInfo.connector} mt-5 mx-2`} />

          {/* SHIPMENT */}
          <div className="flex flex-col items-center gap-1 flex-1 min-w-0">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center ${shipmentInfo.bubble} ${shipmentInfo.bubbleHover}`}
              onClick={() => canVerifyShipment && confirmVerifShipment(row)}
            >
              <i className={`pi ${shipmentInfo.icon} text-white text-sm`}></i>
            </div>

            <span className={`text-xs font-semibold text-center ${shipmentInfo.label}`}>
              {shipmentInfo.status}
            </span>

            {shipmentVerified && (
              <>
                <span className="text-sm font-semibold text-center break-words px-1">
                  {data_verif_shipment_by?.name}
                </span>
                <span className="text-xs font-semibold text-gray-600">
                  {verif_shipment_datetime
                    ? format(new Date(verif_shipment_datetime), "dd/MM/yy HH:mm")
                    : "-"}
                </span>
              </>
            )}
          </div>

          {/* CONNECT → DONE */}
          <div className={`flex-1 h-1 ${doneInfo.connector} mt-5 mx-2`} />

          {/* DONE */}
          <div className="flex flex-col items-center gap-1 flex-1 min-w-0">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center ${doneInfo.bubble} ${doneInfo.bubbleHover}`}
              onClick={() => canVerifyDone && confirmVerifDone(row)}
            >
              <i className={`pi ${doneInfo.icon} text-white text-sm`}></i>
            </div>

            <span className={`text-xs font-semibold text-center ${doneInfo.label}`}>
              {doneInfo.status}
            </span>

            {doneVerified && (
              <>
                <span className="text-sm font-semibold text-center break-words px-1">
                  {data_verif_done_by?.name}
                </span>
                <span className="text-xs font-semibold text-gray-600">
                  {verif_done_datetime
                    ? format(new Date(verif_done_datetime), "dd/MM/yy HH:mm")
                    : "-"}
                </span>
              </>
            )}
          </div>

        </div>
      </div>
    );
  };

  const actionBodyTemplate = (row) => {
    const isOwner = employee?.user_type === 2;
    const isFinance = employee?.user_type === 4;

    const isDone = row.data_verif_done_by && row.verif_done_datetime;

    return (
      <div className="row-center-center gap-1">
        <Button
          icon={`${isDone ? "pi pi-eye" : "pi pi-pencil"}`}
          rounded
          text
          severity="primary"
          onClick={() => {
            if (onClickRowEdit) onClickRowEdit(row);
          }}
          tooltip="Edit"
          tooltipOptions={{ position: 'top' }}
          className="!w-8 !h-8"
        />
        <Button
          icon="pi pi-trash"
          rounded
          text
          severity="danger"
          onClick={() => {
            if (onClickRowDelete) onClickRowDelete(row);
          }}
          tooltip="Delete"
          tooltipOptions={{ position: 'top' }}
          className="!w-8 !h-8"
        />
      </div>
    );
  };

  const MobileCardItem = ({ row }) => {
    const {
      data_created_by,
      created_by_datetime,

      data_verif_owner,
      verif_owner_datetime,

      data_verif_payment_by,
      verif_payment_datetime,

      data_verif_shipment_by,
      verif_shipment_datetime,

      data_verif_done_by,
      verif_done_datetime,

      data_warehouse,
      data_branch,
      number,
      transaction_date,
    } = row;

    const isOwner = employee?.user_type === 2;
    const isFinance = employee?.user_type === 11;
    const isAdmin = employee?.user_type === 4;

    const ownerVerified = !!verif_owner_datetime;
    const paymentVerified = !!verif_payment_datetime;
    const shipmentVerified = !!verif_shipment_datetime;
    const doneVerified = !!verif_done_datetime;

    const isDone = data_verif_done_by && verif_done_datetime;

    // // Unlock rules
    // const canVerifyOwner = isOwner && !ownerVerified;
    // const canVerifyPayment = isFinance && ownerVerified && !paymentVerified;
    // const canVerifyShipment = isAdmin && paymentVerified && !shipmentVerified;
    // const canVerifyDone = isAdmin && shipmentVerified && !doneVerified;

    // Unlock rules
    const canVerifyOwner = isAdmin && !ownerVerified;
    const canVerifyPayment = isAdmin && ownerVerified && !paymentVerified;
    const canVerifyShipment = isAdmin && paymentVerified && !shipmentVerified;
    const canVerifyDone = isAdmin && shipmentVerified && !doneVerified;

    const getStepStatusInfo = (verified, canVerify, step) => {
      const map = {
        owner: {
          icon: "pi-user",
          verifiedText: "Owner Verified",
          verifyText: "Verify Owner",
          pendingText: "Pending",
          bubbleVerified: "bg-green-600",
          bubbleVerify: "bg-green-600 shadow-lg",
          bubblePending: "bg-gray-300",
          label: "text-green-700",
          connector: "bg-green-600",
          hover: "cursor-pointer active:bg-green-700 active:shadow-xl",
        },
        payment: {
          icon: "pi-wallet",
          verifiedText: "Payment Verified",
          verifyText: "Verify Payment",
          pendingText: "Pending",
          bubbleVerified: "bg-blue-600",
          bubbleVerify: "bg-blue-600 shadow-lg",
          bubblePending: "bg-gray-300",
          label: "text-blue-700",
          connector: "bg-blue-600",
          hover: "cursor-pointer active:bg-blue-700 active:shadow-xl",
        },
        shipment: {
          icon: "pi-truck",
          verifiedText: "Shipment Verified",
          verifyText: "Verify Shipment",
          pendingText: "Pending",
          bubbleVerified: "bg-yellow-600",
          bubbleVerify: "bg-yellow-600 shadow-lg",
          bubblePending: "bg-gray-300",
          label: "text-yellow-700",
          connector: "bg-yellow-600",
          hover: "cursor-pointer active:bg-yellow-700 active:shadow-xl",
        },
        done: {
          icon: "pi-check-circle",
          verifiedText: "Completed",
          verifyText: "Finish Task",
          pendingText: "Pending",
          bubbleVerified: "bg-purple-600",
          bubbleVerify: "bg-purple-600 shadow-lg",
          bubblePending: "bg-gray-300",
          label: "text-purple-700",
          connector: "bg-purple-600",
          hover: "cursor-pointer active:bg-purple-700 active:shadow-xl",
        },
      };

      const cfg = map[step] || map.owner;

      if (verified) return { icon: cfg.icon, status: cfg.verifiedText, bubble: cfg.bubbleVerified, label: cfg.label, connector: cfg.connector };
      if (canVerify) return { icon: cfg.icon, status: cfg.verifyText, bubble: cfg.bubbleVerify, label: cfg.label, connector: "bg-gray-300", hover: cfg.hover };
      return { icon: cfg.icon, status: cfg.pendingText, bubble: cfg.bubblePending, label: "text-gray-400", connector: "bg-gray-300" };
    };

    const ownerInfo = getStepStatusInfo(ownerVerified, canVerifyOwner, "owner");
    const paymentInfo = getStepStatusInfo(paymentVerified, canVerifyPayment, "payment");
    const shipmentInfo = getStepStatusInfo(shipmentVerified, canVerifyShipment, "shipment");
    const doneInfo = getStepStatusInfo(doneVerified, canVerifyDone, "done");

    const steps = [
      {
        label: "Created",
        info: { bubble: "bg-gray-500", icon: "pi-user", label: "text-gray-700", connector: "bg-gray-300" },
        name: data_created_by?.name,
        datetime: created_by_datetime,
      },
      { label: ownerInfo.status, info: ownerInfo, canVerify: canVerifyOwner, onClick: () => canVerifyOwner && confirmVerifOwner(row), name: data_verif_owner?.name, datetime: verif_owner_datetime },
      { label: paymentInfo.status, info: paymentInfo, canVerify: canVerifyPayment, onClick: () => canVerifyPayment && confirmVerifPayment(row), name: data_verif_payment_by?.name, datetime: verif_payment_datetime },
      { label: shipmentInfo.status, info: shipmentInfo, canVerify: canVerifyShipment, onClick: () => canVerifyShipment && confirmVerifShipment(row), name: data_verif_shipment_by?.name, datetime: verif_shipment_datetime },
      { label: doneInfo.status, info: doneInfo, canVerify: canVerifyDone, onClick: () => canVerifyDone && confirmVerifDone(row), name: data_verif_done_by?.name, datetime: verif_done_datetime },
    ];

    return (
      <div className="elevated-card p-4 col-start-start gap-4 w-full">

        {/* Header */}
        <div className="col-between-start w-full gap-4">
          <div className="col-start-start gap-1">
            <span className="text-lg font-bold">{data_warehouse?.name}</span>
            <span className="text-sm font-semibold text-gray-600">Branch: {data_branch?.name}</span>
          </div>
          <div className="col-start-start gap-1">
            <span className="text-lg font-bold">{number}</span>
            <span className="text-sm font-semibold text-gray-600">
              Date: {format(new Date(transaction_date), "dd MMMM yyyy")}
            </span>
          </div>
        </div>

        {/* Horizontal Timeline with connectors */}
        <div className="w-full col-start-start">
          <div className="flex items-center justify-between w-full mt-4 relative">
            {steps.map((step, index) => {
              const nextStep = steps[index + 1];
              const connectorColor = nextStep ? nextStep.info?.connector || "bg-gray-300" : "bg-gray-300";

              return (
                <div key={index} className="flex-1 flex flex-col items-center relative">
                  {/* Bubble */}
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${step.info ? step.info.bubble : "bg-gray-500"} ${step.info?.hover || ""} z-10`}
                    onClick={step.onClick}
                  >
                    <i className={`pi ${step.info ? step.info.icon : step.icon} text-white text-sm`} />
                  </div>

                  {/* Connector */}
                  {index < steps.length - 1 && (
                    <div
                      className={`absolute top-5 right-0 w-full h-1 ${connectorColor} z-0`}
                      style={{ left: '50%', right: '-50%' }}
                    ></div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-between w-full mt-4 relative">
            {steps.map((step, index) => (
              <div key={index} className="flex-1 flex flex-col items-center relative">
                {/* Labels */}
                <div className="flex flex-col items-center gap-1 mt-1">
                  <span className={`text-xs font-semibold text-center ${step.info ? step.info.label : "text-gray-700"}`}>{step.label}</span>
                  {step.name ? (
                    <span className="text-sm font-semibold text-center">{step.name}</span>
                  ) : (
                    <span className="text-sm font-semibold text-center">{"\u202F"}</span>
                  )}
                  {step.datetime ? (
                    <span className="text-xs font-semibold text-gray-600">{format(new Date(step.datetime), "dd/MM/yy HH:mm")}</span>
                  ) : (
                    <span className="text-xs font-semibold text-gray-600">{"\u202F"}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="row-center-center gap-3 w-full pt-3 border-t border-gray-200">
          <Button
            icon={isDone ? "pi pi-eye" : "pi pi-pencil"}
            label={isDone ? "View" : "Edit"}
            severity="primary"
            className="flex-1"
            onClick={() => onClickRowEdit && onClickRowEdit(row)}
          />
          <Button
            icon="pi pi-trash"
            label="Delete"
            severity="danger"
            className="flex-1"
            onClick={() => onClickRowDelete && onClickRowDelete(row)}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="col-start-start w-full gap-4">
      {/* filter section */}
      <div className="elevated-card-tight-vertical row-start-start w-full gap-4">
        <StockInFilter
          filterValue={filterValue}
          setFilterValue={setFilterValue}
          onClickRefresh={handleOnClickRefresh}
          filterPendingValue={filterPendingValue}
          setFilterPendingValue={setFilterPendingValue}
        />
      </div>

      {/* Add New Button */}
      <div className="elevated-card-tight-vertical col-start-end w-full gap-2 p-4">
        <div className="row-start-center w-full">
          <Button
            icon="pi pi-plus"
            label="New Request"
            onClick={() => {
              if (onClickAddNew) onClickAddNew();
            }}
          />
        </div>
      </div>

      {/* Pending Requests Section */}
      <div className="elevated-card-tight-vertical col-start-end w-full gap-2">
        <div className="row-start-center w-full gap-2 pb-2 border-b border-gray-200">
          <i className="pi pi-hourglass text-orange-600 text-xl"></i>
          <h3 className="text-lg font-bold text-orange-700">Pending Requests</h3>
          {listDataPending && listDataPending.length > 0 && (
            <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded-full text-sm font-semibold">
              {listDataPending.length}
            </span>
          )}
        </div>

        {isMobile ? (
          // Mobile Card View - Pending
          <div className="col-start-start w-full gap-3">
            {loadingPending ? (
              <div className="col-center-center w-full py-8">
                <i className="pi pi-spin pi-spinner text-3xl text-orange-500"></i>
              </div>
            ) : listDataPending && listDataPending.length > 0 ? (
              listDataPending.map((row) => <MobileCardItem key={row.id} row={row} />)
            ) : (
              <div className="col-center-center w-full py-8 text-gray-500">
                <i className="pi pi-check-circle text-4xl mb-2 text-green-500"></i>
                <span>No pending requests</span>
              </div>
            )}
          </div>
        ) : (
          // Desktop Table View - Pending
          <DataTable
            dataKey="id"
            className="w-100"
            loading={loadingPending}
            showGridlines
            stripedRows
            value={listDataPending}
            emptyMessage="No pending requests"
          >
            <Column
              field="no"
              header="#"
              className="text-center"
              headerClassName="w-[4rem]"
              alignHeader={"center"}
            />
            <Column
              field=""
              header="Action"
              alignHeader={"center"}
              headerClassName="w-[8rem]"
              body={actionBodyTemplate}
            />
            <Column header='Warehouse' field='data_warehouse.name' body={WarehouseBodyTemplate} headerClassName="w-[12rem]" />
            <Column header='Number' field='number' body={NumberBodyTemplate} headerClassName="w-[12rem]" />
            <Column header='Timeline' field='timeline' body={TimelineBodyTemplate} />
          </DataTable>
        )}
      </div>

      {/* Completed Requests Section */}
      <div className="elevated-card-tight-vertical col-start-center w-full gap-2">
        <div className="row-start-center w-full gap-2 pb-2 border-b border-gray-200">
          <i className="pi pi-check-circle text-green-600 text-xl"></i>
          <h3 className="text-lg font-bold text-green-700">Completed Requests</h3>
          {listData && listData.length > 0 && (
            <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-sm font-semibold">
              {listData.length}
            </span>
          )}
        </div>

        {isMobile ? (
          // Mobile Card View - Completed
          <div className="col-start-start w-full gap-3">
            {loading ? (
              <div className="col-center-center w-full py-8">
                <i className="pi pi-spin pi-spinner text-3xl text-green-500"></i>
              </div>
            ) : listData && listData.length > 0 ? (
              listData.map((row) =>
                <MobileCardItem key={row.id} row={row} />
              )
            ) : (
              <div className="col-center-center w-full py-8 text-gray-500">
                <i className="pi pi-inbox text-4xl mb-2"></i>
                <span>No completed requests</span>
              </div>
            )}
          </div>
        ) : (
          // Desktop Table View - Completed
          <DataTable
            dataKey="id"
            className="w-100"
            loading={loading}
            showGridlines
            stripedRows
            value={listData}
            emptyMessage="No completed requests"
          >
            <Column
              field="no"
              header="#"
              className="text-center"
              headerClassName="w-[4rem]"
              alignHeader={"center"}
            />
            <Column
              field=""
              header="Action"
              alignHeader={"center"}
              headerClassName="w-[10rem]"
              body={actionBodyTemplate}
            />
            <Column header='Warehouse' field='data_warehouse.name' body={WarehouseBodyTemplate} headerClassName="w-[12rem]" />
            <Column header='Number' field='number' body={NumberBodyTemplate} headerClassName="w-[12rem]" />
            <Column header='Timeline' field='timeline' body={TimelineBodyTemplate} />
          </DataTable>
        )}

        {isMobile ? (
          <Paginator
            first={parseInt(paging.take) * (paging.page - 1)}
            rows={parseInt(paging.take)}
            totalRecords={parseInt(paging.total)}
            template={{ layout: "PrevPageLink CurrentPageReport NextPageLink" }}
            onPageChange={handleOnPage}
            pt={{
              prevPageButton: {
                className:
                  "bg-red-100 !m-0 !max-w-[22px] !w-[22px] !min-w-[22px] max-h-[22px]",
              },
              nextPageButton: {
                className:
                  "bg-red-100 !m-0 !max-w-[22px] !w-[22px] !min-w-[22px] max-h-[22px]",
              },
              current: {
                className: "bg-red-100 !m-0 max-h-[22px] text-sm",
              },
            }}
          />
        ) : (
          <Paginator
            first={parseInt(paging.take) * (paging.page - 1)}
            rows={parseInt(paging.take)}
            totalRecords={parseInt(paging.total)}
            rowsPerPageOptions={[5, 10, 20, 50]}
            onPageChange={handleOnPage}
          />
        )}
      </div>

      {/* Confirmation Dialog */}
      <ConfirmDialog />
    </div>
  );
});

export default StockInList;
